import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SearchCommandPalette from '../../components/ui/SearchCommandPalette';
import { NotificationProvider } from '../../components/ui/NotificationToast';
import { subUnionsService, articlesService, projectsService, citiesService } from '../../services/dataService';
import MetricsCard from './components/MetricsCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import ChartsSection from './components/ChartsSection';
import RecentContentTable from './components/RecentContentTable';
import Icon from '../../components/AppIcon';
import { formatTime, formatDateWithWeekday } from '../../utils/dateUtils';

const Dashboard = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [metricsData, setMetricsData] = useState([
    { title: 'الاتحادات الفرعية', value: '0', change: '0', changeType: 'neutral', icon: 'Building2', color: 'primary' },
    { title: 'المقالات المنشورة', value: '0', change: '0', changeType: 'neutral', icon: 'FileText', color: 'success' },
    { title: 'المشاريع النشطة', value: '0', change: '0', changeType: 'neutral', icon: 'FolderOpen', color: 'warning' },
    { title: 'المدن المسجلة', value: '0', change: '0', changeType: 'neutral', icon: 'MapPin', color: 'secondary' }
  ]);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Fetch metrics from database
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const [subUnions, articles, projects, cities] = await Promise.all([
          subUnionsService.getAll().catch(() => []),
          articlesService.getAll({ published: true }).catch(() => []),
          projectsService.getAll().catch(() => []),
          citiesService.getAll().catch(() => [])
        ]);

        const activeProjects = projects.filter(p => p.status === 'ongoing' || p.status === 'planned');
        const activeSubUnions = subUnions.filter(u => u.status === 'active');

        setMetricsData([
          {
            title: 'الاتحادات الفرعية',
            value: activeSubUnions.length.toString(),
            change: '+0',
            changeType: 'neutral',
            icon: 'Building2',
            color: 'primary'
          },
          {
            title: 'المقالات المنشورة',
            value: articles.length.toString(),
            change: '+0',
            changeType: 'neutral',
            icon: 'FileText',
            color: 'success'
          },
          {
            title: 'المشاريع النشطة',
            value: activeProjects.length.toString(),
            change: '+0',
            changeType: 'neutral',
            icon: 'FolderOpen',
            color: 'warning'
          },
          {
            title: 'المدن المسجلة',
            value: cities.length.toString(),
            change: '0',
            changeType: 'neutral',
            icon: 'MapPin',
            color: 'secondary'
          }
        ]);
      } catch (err) {
        console.error('Error fetching metrics:', err);
      }
    };

    fetchMetrics();
  }, []);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'k') {
        e?.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };


  return (
    <NotificationProvider>
      <div className="min-h-screen bg-background" dir="rtl">
        {/* Header */}
        <Header 
          onMenuToggle={handleMobileMenuToggle}
          isMenuOpen={isMobileMenuOpen}
        />

        {/* Sidebar */}
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={handleSidebarToggle}
        />

        {/* Main Content */}
        <main className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'
        } mt-16`}>
          <div className="p-6 max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <Breadcrumbs />
              
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mt-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground font-arabic mb-2">
                    لوحة التحكم
                  </h1>
                  <p className="text-muted-foreground font-caption">
                    مرحباً بك في نظام إدارة الإتحاد العام لطلبة سوريا
                  </p>
                </div>
                
                <div className="mt-4 lg:mt-0 flex flex-col items-end space-y-1">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-muted-foreground">
                    <Icon name="Clock" size={16} />
                    <span>{formatTime(currentTime)}</span>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse text-sm text-muted-foreground">
                    <Icon name="Calendar" size={16} />
                    <span>{formatDateWithWeekday(currentTime)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              {metricsData?.map((metric, index) => (
                <MetricsCard
                  key={index}
                  title={metric?.title}
                  value={metric?.value}
                  change={metric?.change}
                  changeType={metric?.changeType}
                  icon={metric?.icon}
                  color={metric?.color}
                />
              ))}
            </div>

            {/* Activity Feed and Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2">
                <ActivityFeed />
              </div>
              <div>
                <QuickActions />
              </div>
            </div>

            {/* Charts Section */}
            <div className="mb-8">
              <ChartsSection />
            </div>

            {/* Recent Content Table */}
            <div className="mb-8">
              <RecentContentTable />
            </div>

            {/* System Status Footer */}
            <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center space-x-4 rtl:space-x-reverse mb-4 md:mb-0">
                  <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
                  <div>
                    <p className="text-sm font-medium text-foreground font-arabic">
                      النظام يعمل بشكل طبيعي
                    </p>
                    <p className="text-xs text-muted-foreground font-caption">
                      آخر تحديث: {formatTime(currentTime)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6 rtl:space-x-reverse text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Icon name="Users" size={16} />
                    <span>1,247 مستخدم نشط</span>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Icon name="Server" size={16} />
                    <span>99.9% وقت التشغيل</span>
                  </div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <Icon name="Database" size={16} />
                    <span>2.4 GB مساحة مستخدمة</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>

        {/* Quick Action Button */}
        <QuickActionButton />

        {/* Search Command Palette */}
        <SearchCommandPalette 
          isOpen={isSearchOpen}
          onClose={() => setIsSearchOpen(false)}
        />

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-200 lg:hidden"
            onClick={handleMobileMenuToggle}
          />
        )}
      </div>
    </NotificationProvider>
  );
};

export default Dashboard;